import { Alert, Badge, Table } from "flowbite-react";
import TitleStripe from "../TitleStripe";
import NoOcurrence from "./NoOcurrence";
import { formatToReal, getImageURLScore } from "../../../utils/utils";
import moment from "moment";
import GraficoConsultasHistorico from "./GraficoConsultasHistorico";
import AvisoCard from "./AvisoCard";

const AvaliaRiscoPJ = ({ data }) => {
  if (!data) {
    return (
      <>
        <TitleStripe>Avalia Risco PJ</TitleStripe>
        <Alert className="mt-2" color="gray" withBorderAccent>
          Consulta indisponível no momento!
        </Alert>
      </>
    );
  }

  const renderTable = (title, headers, rows, renderRow, resumoKey = null) => {
    const resumo =
      resumoKey && Array.isArray(rows) && rows.length > 0
        ? data?.resumoConsulta?.[resumoKey]
        : null;
    return (
      <>
        <TitleStripe>{title}</TitleStripe>
        {resumo && (
          <div className="text-sm text-gray-700 px-1 flex gap-10">
            <div className="text-red-600 flex gap-1 items-center">
              <strong>Quantidade Total:</strong>{" "}
              <Badge className="w-fit" color="failure">
                {resumo.quantidadeTotal || 0}
              </Badge>
            </div>
            {"valorTotal" in resumo && (
              <div className="text-red-600 flex gap-1 items-center">
                <strong>Valor Total:</strong>{" "}
                <Badge className="w-fit" color="failure">
                  {formatToReal(resumo.valorTotal)}
                </Badge>
              </div>
            )}
          </div>
        )}
        {Array.isArray(rows) && rows.length > 0 ? (
          <>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <Table striped>
                <Table.Head className="text-black">
                  {headers.map((header, idx) => (
                    <Table.HeadCell
                      key={idx}
                      className="text-center"
                      style={{ background: "#3EED98" }}
                    >
                      {header}
                    </Table.HeadCell>
                  ))}
                </Table.Head>
                <Table.Body className="divide-y">
                  {rows.map((item, idx) => renderRow(item, idx))}
                </Table.Body>
              </Table>
            </div>
            <span className="-mt-2 block text-xs italic">
              Exibindo as últimas 5 informações
            </span>
          </>
        ) : (
          <div className="-mt-3">
            <NoOcurrence />
          </div>
        )}
      </>
    );
  };

  const quadroSocietario = Array.isArray(data?.quadroSocietario)
    ? data.quadroSocietario
    : [];
  const diretoria = Array.isArray(data?.diretoria) ? data.diretoria : [];
  const chequesSustados = Array.isArray(data?.chequesSustados)
    ? data.chequesSustados
    : [];
  const historicoConsultas = Array.isArray(data?.ultimasConsultas)
    ? data.ultimasConsultas
    : [];

  return (
    <>
      <div className="mt-6 border rounded-2xl shadow p-6 bg-white">
        <h3 className="text-lg font-bold mb-4 border-b pb-2">
          📋 Dados Cadastrais
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
          {[
            "razaoSocial",
            "nomeFantasia",
            "cnpj",
            "inscricaoEstadual",
            "situacao",
            "naturezaJuridica",
            "ramoAtividade",
            "dataFundacao",
            "endereco",
            "bairro",
            "cidade",
            "uf",
            "cep",
          ].map((key) => (
            <div key={key}>
              <strong>
                {key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
                :
              </strong>{" "}
              {data?.dadosCadastrais?.[key] || "Indisponível"}
            </div>
          ))}
        </div>
      </div>

      <TitleStripe>Score</TitleStripe>
      <div className="my-4">
        <img
          className="mx-auto max-w-xs"
          src={getImageURLScore(+data?.score?.score)}
        />
        <div className="font-semibold text-2xl text-center">
          {data?.score?.score}
        </div>
        <p className="text-center text-sm text-gray-600 max-w-xl mx-auto border border-gray-200 bg-gray-50 p-2 mt-4">
          {data?.score?.mensagem}
        </p>
      </div>

      {renderTable(
        "Histórico de Consultas",
        ["Data", "Empresa"],
        historicoConsultas,
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.data}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.empresa}</Table.Cell>
          </Table.Row>
        )
      )}

      <TitleStripe>Quadro de avisos</TitleStripe>
      <div className="container-alerts three-columns">
        {[
          "protestos",
          "pendenciasFinanceiras",
          "chequesSemFundo",
          "chequesSustados",
        ].map((key) => (
          <AvisoCard
            key={key}
            title={key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase())}
            hasRestrictions={+data?.resumoConsulta?.[key]?.quantidadeTotal > 0}
            messageError={`${
              data?.resumoConsulta?.[key]?.quantidadeTotal || 0
            } ocorrências`}
          />
        ))}
      </div>

      {renderTable(
        "Pendências Financeiras",
        ["Data", "Informante", "Valor", "Tipo", "Doc. Origem"],
        data?.pendenciasFinanceiras ?? [],
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.data}</Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.informante}
            </Table.Cell>
            <Table.Cell className="text-center py-1">
              {formatToReal(item.valor)}
            </Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.tipoPendencia}
            </Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.documentoOrigem}
            </Table.Cell>
          </Table.Row>
        ),
        "pendenciasFinanceiras"
      )}

      {renderTable(
        "Protestos",
        ["Data", "Cartório", "Valor", "Cidade", "Estado"],
        data?.protestos ?? [],
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.data}</Table.Cell>
            <Table.Cell className="text-center py-1">
              {item.cartorio}
            </Table.Cell>
            <Table.Cell className="text-center py-1">{item.valor}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.cidade}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.estado}</Table.Cell>
          </Table.Row>
        ),
        "protestos"
      )}

      {renderTable(
        "Cheques Sem Fundo",
        ["Data", "Banco", "Agência", "Motivo", "Qtd"],
        data?.chequesSemFundo ?? [],
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">{item.data}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.banco}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.agencia}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.motivo}</Table.Cell>
            <Table.Cell className="text-center py-1">{item.valor}</Table.Cell>
          </Table.Row>
        ),
        "chequesSemFundo"
      )}

      {renderTable(
        "Cheques Sustados",
        ["Mensagem"],
        chequesSustados,
        (item, idx) => (
          <Table.Row key={idx} className="bg-white text-black">
            <Table.Cell className="text-center py-1">
              {item.mensagem}
            </Table.Cell>
          </Table.Row>
        ),
        "chequesSustados"
      )}
    </>
  );
};

export default AvaliaRiscoPJ;
